import PropTypes from 'prop-types';

/**
 * The contract between the redesigned project dashboard (ProjectOverview)
 * and its modules — the sections under the header. See README.md in this
 * folder.
 */

/**
 * The dashboard is a grid of cells, three columns of three rows. A module
 * covers `size.w` × `size.h` cells from the one the layout puts it in
 * (dashboardLayout.js).
 */
export const DASHBOARD_GRID_SIZE = Object.freeze({ columns: 3, rows: 3 });

/**
 * - VIEW: the saved layout, every module as it is (the grid from md up, a
 *   stack below it); modules the user does not get leave their cells empty.
 * - EDIT: the editor (from md up): the whole 3×3 grid, every placed module
 *   in it whatever its access.
 */
export const DASHBOARD_MODES = Object.freeze({
  VIEW: 'view',
  EDIT: 'edit',
});

/**
 * What a module's `getAccess(context)` answers:
 * - OK: the user gets the module; it renders its Component;
 * - NO_PERMISSION: the user lacks a permission it needs (or the server said
 *   so: an attention list's `noAccess`);
 * - NOT_IN_USE: nothing of it on this project (another project than a
 *   worksite, a form folder the project hides);
 * - PENDING: not known yet — the user's permissions still loading; it
 *   renders its Preview, animated, in its place.
 */
export const MODULE_ACCESS = Object.freeze({
  OK: 'OK',
  NO_PERMISSION: 'NO_PERMISSION',
  NOT_IN_USE: 'NOT_IN_USE',
  PENDING: 'PENDING',
});

/**
 * Whether a module the user lacks a permission for shows the "no permission"
 * placeholder (ModuleAccessPlaceholder) in view mode. Off for now: such a
 * module is hidden as one not in use is (the user's decision); switching it
 * on is this one constant.
 */
export const SHOW_NO_PERMISSION_PLACEHOLDER = false;

/**
 * Whether a module of this access takes its cells in view mode. The others
 * leave them empty (and a row or column with nothing shown closes up).
 * @param {string} access a MODULE_ACCESS value
 * @param {boolean} [showNoPermission] SHOW_NO_PERMISSION_PLACEHOLDER
 * @returns {boolean}
 */
export const isShownInView = (
  access,
  showNoPermission = SHOW_NO_PERMISSION_PLACEHOLDER
) =>
  access === MODULE_ACCESS.OK ||
  access === MODULE_ACCESS.PENDING ||
  (showNoPermission && access === MODULE_ACCESS.NO_PERMISSION);

/**
 * Whether the editor's palette offers a module on this project: its
 * `isOffered(context)` when it has one (no module of the catalogue has one
 * today), otherwise always. The palette asks on every render, so a module
 * comes in once what decides it does. A module already placed stays placed
 * either way.
 * @param {Object} module a registry entry
 * @param {Object} context useDashboardModuleContext's result
 * @returns {boolean}
 */
export const isModuleOffered = (module, context) =>
  typeof module.isOffered === 'function'
    ? Boolean(module.isOffered(context))
    : true;

/**
 * The size a module draws itself at — the one prop that says how much room
 * it has and what to show in it (README.md, "Sizes"):
 * - MOBILE (`'mobile'`): on a phone (below sm), stacked: its title row and
 *   one row of data, at its own height;
 * - S1 / S2 / S3 (`'1x1'`, `'1x2'`, `'1x3'`): one, two or three cells tall
 *   (the layout item's own height): in the grid from md up, and stacked on
 *   sm–md. The module stands in a box of a fixed height (its
 *   `contentHeight`) and takes all of it, its lists scrolling within: a 1×1
 *   the most important figures only, a 1×2 / 1×3 longer lists and charts.
 */
export const MODULE_SIZES = Object.freeze({
  MOBILE: 'mobile',
  S1: '1x1',
  S2: '1x2',
  S3: '1x3',
});

/** Every MODULE_SIZES value, smallest first. */
export const MODULE_SIZE_VALUES = Object.freeze([
  MODULE_SIZES.MOBILE,
  MODULE_SIZES.S1,
  MODULE_SIZES.S2,
  MODULE_SIZES.S3,
]);

const ROWS_BY_SIZE = Object.freeze({
  [MODULE_SIZES.S1]: 1,
  [MODULE_SIZES.S2]: 2,
  [MODULE_SIZES.S3]: 3,
});

/**
 * The heights every module may take, one to three cells (a layout item's
 * `h`): each entry's `resize`. Its default height is its `size.h`.
 */
export const MODULE_RESIZE = Object.freeze({
  minH: 1,
  maxH: DASHBOARD_GRID_SIZE.rows,
});

/**
 * The size the dashboard gives a module's cell: `'mobile'` on a phone,
 * otherwise `'1x' + its height in cells` — the layout item's own height
 * (`cell.size.h`, buildDashboardGrid's footprint), never the registry's
 * default.
 * @param {{size?: {h: number}, module?: {size: {h: number}}}} cell a module cell of buildDashboardGrid
 * @param {{isPhone?: boolean}} [options]
 * @returns {'mobile'|'1x1'|'1x2'|'1x3'}
 */
export const getModuleSize = (cell, { isPhone = false } = {}) => {
  if (isPhone) return MODULE_SIZES.MOBILE;
  const footprint = (cell && cell.size) || (cell && cell.module?.size);
  const rows = Number.isInteger(footprint?.h) ? footprint.h : 1;
  return `1x${Math.min(Math.max(rows, 1), DASHBOARD_GRID_SIZE.rows)}`;
};

/**
 * How many cells tall a size is: 1, 2 or 3; null for `'mobile'` (its own
 * height) or anything else.
 * @param {string} size a MODULE_SIZES value
 * @returns {number|null}
 */
export const getSizeRows = (size) => ROWS_BY_SIZE[size] || null;

/**
 * Whether a module of this size stands in a box of a fixed height (its
 * `contentHeight`) and takes all of it (`height: 100%`, scrolling within):
 * every size but `'mobile'`, which is its own height.
 * @param {string} size a MODULE_SIZES value
 * @returns {boolean}
 */
export const isSizedToCell = (size) => getSizeRows(size) !== null;

/**
 * Whether a size is a small one, `'mobile'` or `'1x1'`: only the most
 * important figures, and the section's title row dense
 * (`OverviewSectionTitle dense`), as the blocks and the phone's rows have it.
 * @param {string} size a MODULE_SIZES value
 * @returns {boolean}
 */
export const isSmallSize = (size) =>
  size === MODULE_SIZES.MOBILE || size === MODULE_SIZES.S1;

/** A MODULE_SIZES value. */
export const moduleSizePropType = PropTypes.oneOf(MODULE_SIZE_VALUES);

/**
 * The shared data of the dashboard (useDashboardModuleContext) that
 * `getAccess(context)` / `isOffered(context)` read and every module
 * component gets as `context`.
 */
export const dashboardContextPropType = PropTypes.shape({
  project: PropTypes.object.isRequired,
  /** A worksite project (businessTypeEnum NORMAL). */
  isWorksite: PropTypes.bool.isRequired,
  /** All the user's resources in the project are RESIDENT. */
  isResident: PropTypes.bool.isRequired,
  /** The user's permissions have come in (or failed to: then none). */
  permissionsKnown: PropTypes.bool.isRequired,
  /** Whether the user has a permission a module declared in `permissions`. */
  can: PropTypes.func.isRequired,
  /**
   * The user is an admin of some business (selectUserAdminBusinessIds): the
   * API gives them their businesses' workers' records. Always given by
   * useDashboardModuleContext; left out, not an admin.
   */
  isBusinessAdmin: PropTypes.bool,
  /** useDashboardGauges' result: the project's sensor reading. */
  gauges: PropTypes.shape({
    reading: PropTypes.object,
    deviceName: PropTypes.string,
    loaded: PropTypes.bool,
    turnedOff: PropTypes.bool,
  }).isRequired,
  /** The forms' lists (projectOverview slice entries), by kind. */
  formLists: PropTypes.shape({
    trMeters: PropTypes.object,
    nonConformance: PropTypes.object,
  }).isRequired,
  /** The project's orientation and the actions that open it. */
  orientation: PropTypes.shape({
    required: PropTypes.bool,
    formRequired: PropTypes.bool,
    onOpen: PropTypes.func,
    onOpenForm: PropTypes.func,
  }).isRequired,
});

/**
 * The px height a module's box gives it, `contentHeight`
 * (getModuleContentHeight in dashboardGrid.js): a 1×1 its block's
 * (`blockHeight`, 116 or 88); a 1×2 / 1×3 its cells' content — in the grid
 * the rows it spans (253 / 362 from the first of the default rows, 225 for a
 * 1×2 from the second), stacked on sm–md 253 / 362; null on `'mobile'` (its
 * own height). Lay out within it: what does not fit scrolls (a list) or is
 * left out (a chart).
 */
const contentHeightPropType = PropTypes.number;

/** The props every module component (`Component`) gets. */
export const dashboardModulePropTypes = {
  project: PropTypes.object.isRequired,
  /** What to draw: `'mobile'`, `'1x1'`, `'1x2'` or `'1x3'` (MODULE_SIZES). */
  size: moduleSizePropType.isRequired,
  /** The room its box gives it in px; null on `'mobile'`. */
  contentHeight: contentHeightPropType,
  context: dashboardContextPropType.isRequired,
};

/**
 * The props a module's `Preview` gets: its skeleton look at that size, the
 * title its only text. Animated while the module is PENDING in view mode;
 * the editor shows it inside NoAnimationSkeletons.
 */
export const dashboardModulePreviewPropTypes = {
  /** The module's registry entry (its title, size, blockHeight). */
  module: PropTypes.object.isRequired,
  /** As the Component's. */
  size: moduleSizePropType.isRequired,
  /** As the Component's. */
  contentHeight: contentHeightPropType,
  context: dashboardContextPropType,
};

/** A module entry of the registry. */
export const dashboardModulePropType = PropTypes.shape({
  id: PropTypes.string.isRequired,
  titleKey: PropTypes.string.isRequired,
  titleDefault: PropTypes.string.isRequired,
  /** Its default footprint (the palette's, the default layout's). */
  size: PropTypes.shape({
    w: PropTypes.number.isRequired,
    h: PropTypes.number.isRequired,
  }).isRequired,
  /**
   * The heights a layout item of it may take (`h`), `size.h` its default:
   * MODULE_RESIZE (1–3) for every module. The editor's corner handle
   * changes it.
   */
  resize: PropTypes.shape({
    minH: PropTypes.number.isRequired,
    maxH: PropTypes.number.isRequired,
  }),
  /** Its block's height as a 1×1 (116 or 88): the row's height in the grid. */
  blockHeight: PropTypes.number.isRequired,
  permissions: PropTypes.arrayOf(PropTypes.string).isRequired,
  getAccess: PropTypes.func.isRequired,
  isOffered: PropTypes.func,
  /** Draws it at a size: dashboardModulePropTypes. */
  Component: PropTypes.elementType.isRequired,
  /** Its skeleton look at a size: dashboardModulePreviewPropTypes. */
  Preview: PropTypes.elementType.isRequired,
});
